import { useState, useEffect, useCallback } from "react";
import {
  getHistory,
  getJobStats,
  getSubscriptionStatus,
} from "../services/dashboardService";

// Module-level cache — survives navigation (component remounts) within the same session.
let _subStatusCache = undefined;

const RECENT_LIMIT = 10;

const formatDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart}`;
};

// Safe short-date — returns "—" for missing/invalid timestamps.
const formatShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Human-readable run time from milliseconds: null, "52s", or "1m 3s".
const formatDuration = (ms) => {
  if (ms == null || Number.isNaN(Number(ms))) return null;
  const totalSec = Math.round(Number(ms) / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const groupHistoryItems = (historyItems) => {
  const assetMap = new Map();
  historyItems.forEach((h) => {
    const key = h.asset_id ?? h.id;
    const ver = {
      key:        `v${h.version ?? 1}`,
      label:      `V${h.version ?? 1}`,
      latest:     h.revision?.is_latest ?? false,
      date:       formatDT(h.revision?.created_at || h.created_at),
      revisionId: h.revision_id ?? h.revision?.id ?? null,
    };
    if (assetMap.has(key)) {
      assetMap.get(key).versions.push(ver);
    } else {
      const docMeta = h.revision?.documents?.[0];
      assetMap.set(key, {
        company:    h.software_name,
        date:       formatDT(h.created_at),
        runtime:    formatDuration(h.revision?.duration_ms),
        document:   docMeta ? { id: docMeta.id, version: docMeta.version, fileName: docMeta.file_name, mimeType: docMeta.mime_type } : null,
        url:        h.privacy_url,
        score:      h.overall_score,
        revisionId: h.revision_id ?? h.revision?.id ?? null,
        assetId:    key,
        versions:   [ver],
      });
    }
  });
  const grouped = Array.from(assetMap.values());
  grouped.forEach((row) => {
    row.versions.sort((a, b) => {
      const na = parseInt(a.key.replace("v", ""), 10) || 0;
      const nb = parseInt(b.key.replace("v", ""), 10) || 0;
      return nb - na;
    });
    row.versions.forEach((v, i) => { v.latest = i === 0; });
    row.revisionId = row.versions[0]?.revisionId ?? row.revisionId;
  });
  return grouped;
};

export const useDashboard = () => {
  const [kpis, setKpis]                           = useState(null);
  const [lastActive, setLastActive]               = useState("—");
  const [recent, setRecent]                       = useState([]);
  const [recentPage, setRecentPage]               = useState(1);
  const [recentHasMore, setRecentHasMore]         = useState(false);
  const [recentLoadingMore, setRecentLoadingMore] = useState(false);
  const [ongoing, setOngoing]                     = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    _subStatusCache !== undefined ? _subStatusCache : null
  );

  useEffect(() => {
    (async () => {
      try {
        const fetchSub = _subStatusCache === undefined;

        const [statsResult, histResult, ongoingResult, subStatusResult] = await Promise.allSettled([
          getJobStats(),
          getHistory({ page: 1, limit: RECENT_LIMIT }),
          getHistory({ page: 1, limit: 20, analysis_status: 'processing' }),
          fetchSub ? getSubscriptionStatus() : Promise.resolve({ data: _subStatusCache }),
        ]);

        // ── KPIs ──────────────────────────────────────────────────────────────
        if (statsResult.status === "fulfilled") {
          const statsData = statsResult.value?.data ?? {};
          const counts    = statsData.job_counts ?? {};
          const total     = statsData.total_jobs ?? 0;
          const completed = counts.completed ?? 0;
          const running   = (counts.running ?? 0) + (counts.queued ?? 0);
          const assetsRemaining = statsData.assets?.remaining ?? null;
          const assetsLimit     = statsData.assets?.limit ?? null;
          const loginDate = statsData.last_login_date ?? "";
          const loginTime = statsData.last_login_time ?? "";
          setLastActive(
            statsData.last_login_at
              ? formatDT(statsData.last_login_at)
              : (loginDate && loginTime ? `${loginDate} · ${loginTime}` : "—")
          );
          setKpis({
            completed: { value: String(completed), sub: `${completed} of ${total} total` },
            ongoing:   { value: String(running),   sub: running === 1 ? "1 in progress" : `${running} in progress` },
            remaining: {
              value: assetsRemaining !== null ? String(assetsRemaining) : "—",
              sub: assetsLimit !== null ? `out of ${assetsLimit} allowed` : "of monthly allowance",
              limit: assetsLimit,
            },
          });
        } else {
          setLastActive("—");
          setKpis({
            completed: { value: "—", sub: "" },
            ongoing:   { value: "—", sub: "" },
            remaining: { value: "—", sub: "" },
          });
        }

        // ── Recent analyses ───────────────────────────────────────────────────
        if (histResult.status === "fulfilled") {
          const historyItems = histResult.value?.data?.history ?? [];
          const meta         = histResult.value?.meta?.pagination ?? {};
          setRecent(groupHistoryItems(historyItems));
          setRecentPage(1);
          setRecentHasMore((meta.totalPages ?? 1) > 1);
        }

        // ── Ongoing analyses ──────────────────────────────────────────────────
        if (ongoingResult.status === "fulfilled") {
          const ongoingItems = ongoingResult.value?.data?.history ?? [];
          setOngoing(
            ongoingItems.map((h) => ({
              id:         h.id,
              assetId:    h.asset_id ?? h.asset?.id ?? null,
              company:    h.software_name ?? "—",
              date:       formatShortDate(h.created_at),
              privacyUrl: h.privacy_url ?? "",
              version:    h.version ?? 1,
            }))
          );
        }

        // ── Subscription status ───────────────────────────────────────────────
        if (subStatusResult.status === "fulfilled") {
          const rawBody = subStatusResult.value?.data ?? {};
          const normalized = rawBody?.plan_status !== undefined
            ? rawBody
            : (rawBody?.data ?? rawBody);
          _subStatusCache = normalized;
          setSubscriptionStatus(normalized);
        } else {
          _subStatusCache = {};
          setSubscriptionStatus({});
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadMoreRecent = useCallback(async () => {
    if (recentLoadingMore || !recentHasMore) return;
    setRecentLoadingMore(true);
    try {
      const nextPage = recentPage + 1;
      const res = await getHistory({ page: nextPage, limit: RECENT_LIMIT });
      const historyItems = res?.data?.history ?? [];
      const meta         = res?.meta?.pagination ?? {};
      setRecent((prev) => {
        const merged = [...prev];
        groupHistoryItems(historyItems).forEach((newRow) => {
          const existing = merged.find((r) => r.assetId === newRow.assetId);
          if (existing) {
            newRow.versions.forEach((v) => {
              if (!existing.versions.find((ev) => ev.key === v.key)) {
                existing.versions.push(v);
              }
            });
            existing.versions.sort((a, b) => {
              const na = parseInt(a.key.replace("v", ""), 10) || 0;
              const nb = parseInt(b.key.replace("v", ""), 10) || 0;
              return nb - na;
            });
            existing.versions.forEach((v, i) => { v.latest = i === 0; });
            existing.revisionId = existing.versions[0]?.revisionId ?? existing.revisionId;
          } else {
            merged.push(newRow);
          }
        });
        return merged;
      });
      setRecentPage(nextPage);
      setRecentHasMore(nextPage < (meta.totalPages ?? 1));
    } finally {
      setRecentLoadingMore(false);
    }
  }, [recentPage, recentHasMore, recentLoadingMore]);

  return { kpis, lastActive, recent, recentHasMore, recentLoadingMore, loadMoreRecent, ongoing, loading, subscriptionStatus };
};
